import axiosInstance, { endpoints } from 'src/lib/axios';

import {
  bokfördaArbetsgivardeklarationer,
  inlämnadeMomsdeklarationer,
  inlämnadeInkomstdeklarationer,
  genomfördaStämmor,
} from './avklarat-mappning';
import { hämtaArkiv } from 'src/sections/styrelse/StyrelseService';

/**
 * Uppgifter som redan är utförda enligt appens egen data.
 *
 * En arbetsgivardeklaration, momsdeklaration, inkomstdeklaration eller stämma
 * som är bokförd, inlämnad respektive genomförd behöver inte bockas av för hand
 * — den är bevisligen gjord.
 *
 * Själva nyckelmappningen ligger i `avklarat-mappning.ts` och är testad där.
 */

export type Avklarat = {
  /** Uppgifts-id → varför den räknas som klar. Nyckeln matchar `Myndighetsuppgift.id`. */
  anledningPerUppgift: Record<string, string>;
  /**
   * Om organisationen har några arbetsgivardeklarationer inlästa alls, bokförda
   * eller ej. Signal till `bedömArbetsgivare` om att organisationen faktiskt är
   * arbetsgivare. undefined när hämtningen inte gick igenom.
   */
  harArbetsgivardeklarationer?: boolean;
};

export const INGET_AVKLARAT: Avklarat = { anledningPerUppgift: {} };

type AgdResultat = { avklarat: Record<string, string>; finnsNågon: boolean };

async function hämtaArbetsgivardeklarationer(orgId: string): Promise<AgdResultat> {
  const svar = await axiosInstance.get(`${endpoints.skatt.arbetsgivardeklarationer.root}/${orgId}`);
  const lista = svar?.data?.arbetsgivardeklarationer;
  return {
    avklarat: bokfördaArbetsgivardeklarationer(lista),
    finnsNågon: Array.isArray(lista) && lista.length > 0,
  };
}

async function hämtaMomsdeklarationer(orgId: string): Promise<Record<string, string>> {
  const svar = await axiosInstance.get(endpoints.momsdeklaration.hämta(orgId));
  return inlämnadeMomsdeklarationer(svar?.data);
}

async function hämtaInkomstdeklarationer(orgId: string): Promise<Record<string, string>> {
  const svar = await axiosInstance.get(endpoints.skatt.inkomstdeklarationer.hämta(orgId));
  return inlämnadeInkomstdeklarationer(svar?.data);
}

async function hämtaStämmor(orgId: string): Promise<Record<string, string>> {
  const svar = await hämtaArkiv(orgId);
  return genomfördaStämmor(svar);
}

/**
 * Hämtar alla källor. En källa som fallerar tar inte de andra med sig, och
 * ett totalfel ger tom avbockning — uppgiften visas då som öppen, vilket är
 * rätt sida att fela på för en påminnelselista.
 */
export async function hämtaAvklarat(orgId: string): Promise<Avklarat> {
  const [agd, moms, ink, stammor] = await Promise.allSettled([
    hämtaArbetsgivardeklarationer(orgId),
    hämtaMomsdeklarationer(orgId),
    hämtaInkomstdeklarationer(orgId),
    hämtaStämmor(orgId),
  ]);

  return {
    anledningPerUppgift: {
      ...(agd.status === 'fulfilled' ? agd.value.avklarat : {}),
      ...(moms.status === 'fulfilled' ? moms.value : {}),
      ...(ink.status === 'fulfilled' ? ink.value : {}),
      ...(stammor.status === 'fulfilled' ? stammor.value : {}),
    },
    harArbetsgivardeklarationer: agd.status === 'fulfilled' ? agd.value.finnsNågon : undefined,
  };
}
